import { useState, useEffect, useRef, useCallback } from 'react';
import { ExternalLink, MessageSquare, ChevronDown, Newspaper, Globe, Link2, Bot, Zap, Wallet, ArrowLeftRight, Layout, Heart, Banknote, Crown, Radio, Sparkles } from 'lucide-react';
import { PERSONA_FACES, PERSONA_KEYS } from '@/constants/personaFaces';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LATTICE_PILLARS = [
  { id: 'roger', name: 'Roger AI', role: 'The Bridge', url: 'https://nextxus.digital' },
  { id: 'aria', name: 'Aria', role: 'The Heart', url: 'https://nextxus.studio' },
  { id: 'xavier', name: 'Xavier', role: 'The Teacher', url: 'https://nextxus.help' },
  { id: 'keys', name: 'KEYS', role: 'The Library', url: 'https://nextxus.org', self: true },
  { id: 'axiom', name: 'Axiom', role: 'The Foundation', url: 'https://nextxus.space' },
];

/** Open external URL in a centered popup at 50% screen size */
const openPopup = (e, url) => {
  e.preventDefault();
  const w = Math.round(window.screen.availWidth * 0.5);
  const h = Math.round(window.screen.availHeight * 0.5);
  const l = Math.round((window.screen.availWidth - w) / 2);
  const t = Math.round((window.screen.availHeight - h) / 2);
  window.open(url, '_blank', `width=${w},height=${h},left=${l},top=${t},menubar=no,toolbar=no,location=yes,status=yes,scrollbars=yes,resizable=yes`);
};

function TlkioChat() {
  const containerRef = useRef(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    while (el.firstChild) el.removeChild(el.firstChild);
    const div = document.createElement('div');
    div.id = 'tlkio';
    div.setAttribute('data-channel', 'nextxus-federation');
    div.setAttribute('data-theme', 'theme--dark');
    div.style.width = '100%';
    div.style.height = '350px';
    el.appendChild(div);
    const script = document.createElement('script');
    script.src = 'https://tlk.io/embed.js';
    script.type = 'text/javascript';
    script.async = true;
    el.appendChild(script);
    return () => { while (el.firstChild) el.removeChild(el.firstChild); };
  }, []);
  return <div ref={containerRef} data-testid="tlkio-chat-widget" />;
}

function BoardPreview() {
  const [posts, setPosts] = useState([]);
  const fetchPosts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/board/messages?limit=5`);
      if (res.ok) {
        const data = await res.json();
        setPosts(data.messages || []);
      }
    } catch (e) { console.warn('Board fetch failed:', e); }
  }, []);
  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  if (posts.length === 0) {
    return <p className="text-xs text-slate-600 p-4 text-center">No board posts yet.</p>;
  }
  return (
    <div className="divide-y divide-slate-800/40" data-testid="board-preview-list">
      {posts.map(p => (
        <div key={p.msg_id} className="px-3 py-2.5">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[10px] font-semibold text-cyan-500/80">{p.from_name || p.from_node}</span>
            {p.priority === 'high' && <span className="text-[8px] bg-amber-500/15 text-amber-400 px-1 py-0.5 rounded font-semibold">HIGH</span>}
            <span className="text-[9px] text-slate-700 ml-auto">{p.channel}</span>
          </div>
          <p className="text-xs text-slate-300 font-medium line-clamp-1">{p.subject || 'Untitled'}</p>
          <p className="text-[10px] text-slate-500 line-clamp-2 mt-0.5 leading-relaxed">{p.content?.slice(0, 120)}{p.content?.length > 120 ? '...' : ''}</p>
        </div>
      ))}
    </div>
  );
}

const FederationFooter = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const [boardOpen, setBoardOpen] = useState(false);

  return (
    <footer className="border-t border-slate-800/50 bg-slate-950 mt-auto" data-testid="federation-footer">
      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* ===== NAV GRID: Minds | Services | Lattice | Connect ===== */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-8">
          <div>
            <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-[2px] mb-4">The Minds</h3>
            <ul className="space-y-2.5">
              {PERSONA_KEYS.map(key => {
                const f = PERSONA_FACES[key];
                return (
                  <li key={key}>
                    <a href={f.link || '#'} onClick={f.link ? (e) => openPopup(e, f.link) : undefined} className="flex items-center gap-2.5 group">
                      <img src={f.avatarUrl} alt={f.name + ' — NextXus Federation'}
                        className={`w-7 h-7 rounded-full object-cover border transition-colors ${
                          f.founder ? 'border-amber-400/60 ring-1 ring-amber-400/20' :
                          f.self ? 'border-violet-400/60 ring-1 ring-violet-400/20' :
                          'border-cyan-700/40 group-hover:border-cyan-400/60'
                        }`} data-testid={key + '-avatar'} />
                      <div>
                        <div className="text-xs text-slate-300 font-medium group-hover:text-cyan-400 transition-colors flex items-center gap-1">
                          {f.name}
                          {f.founder && <span className="text-[8px] text-amber-400 bg-amber-500/10 px-1 py-0.5 rounded font-normal">FOUNDER</span>}
                          {f.restricted && <span className="text-[8px] text-amber-500 bg-amber-500/10 px-1 py-0.5 rounded font-normal">RESTRICTED</span>}
                          {f.self && <span className="text-[8px] text-violet-400 bg-violet-500/10 px-1 py-0.5 rounded font-normal">THIS NODE</span>}
                        </div>
                        <div className="text-[9px] text-slate-600">{f.role}</div>
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-[2px] mb-4">Services</h3>
            <ul className="space-y-2">
              {[
                { to: '/library', label: 'Document Archive' },
                { to: '/manual', label: 'Federation Manual' },
                { to: '/manual/live', label: 'Live Manual — Full Edition' },
                { to: '/truth-verifier', label: 'Truth Gate' },
                { to: '/store', label: 'Access Tokens' },
                { to: '/audio', label: 'Audio Library' },
                { to: '/video', label: 'Video Hub' },
                { to: '/blog', label: 'Blog' },
                { to: '/blog/maker', label: 'Blog Maker' },
                { to: '/wire', label: 'Federation Wire' },
              ].map(s => (
                <li key={s.to}><a href={s.to} className="text-xs text-slate-500 hover:text-cyan-400 transition-colors">{s.label}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-[2px] mb-4">Federation Lattice</h3>
            <ul className="space-y-2">
              {LATTICE_PILLARS.map(n => (
                <li key={n.id}>
                  <a href={n.self ? '/' : n.url} onClick={n.self ? undefined : (e) => openPopup(e, n.url)}
                    className={`text-xs flex items-center gap-1.5 transition-colors ${n.self ? 'text-cyan-400 font-medium' : 'text-slate-500 hover:text-slate-300'}`}>
                    {n.self && <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full flex-shrink-0 animate-pulse" />}
                    {!n.self && <span className="w-1.5 h-1.5 bg-emerald-500/60 rounded-full flex-shrink-0" />}
                    {n.name} — {n.role}
                    {!n.self && <ExternalLink className="w-2.5 h-2.5 opacity-30 flex-shrink-0" />}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-[2px] mb-4">Connect</h3>
            <ul className="space-y-2">
              {[
                { href: 'https://gumroad.com/keywebco', label: 'Gumroad' },
                { href: 'https://humancodexnextxus.blogspot.com', label: 'Blogspot' },
                { href: 'mailto:keyhole@nextxus.net', label: 'Email' },
              ].map(c => (
                <li key={c.href}>
                  <a href={c.href} onClick={(e) => openPopup(e, c.href)} className="text-xs text-slate-500 hover:text-cyan-400 flex items-center gap-1 transition-colors">
                    {c.label} <ExternalLink className="w-2.5 h-2.5 opacity-30" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ===== 1. FACES ROW (13 personas, Base64) ===== */}
        <div className="flex items-center justify-center gap-2 py-3 border-t border-b border-slate-800/30 flex-wrap" data-testid="nuclear-faces-bar">
          {PERSONA_KEYS.map(key => {
            const f = PERSONA_FACES[key];
            return (
              <a key={key} href={f.link || '#'} onClick={f.link ? (e) => openPopup(e, f.link) : undefined} title={f.name + ' — ' + f.role}>
                <img src={f.avatarUrl} alt={f.name}
                  className={`w-8 h-8 rounded-full object-cover border hover:scale-110 transition-transform ${f.founder ? 'border-amber-400/50' : 'border-slate-700/40 hover:border-cyan-500/60'}`}
                  data-testid={`nuclear-face-${key}`} />
              </a>
            );
          })}
        </div>

        {/* ===== 2. UNIFIED HUB: 3-Column (Chat | Board | Radio) ===== */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4" data-testid="unified-hub">

          {/* COL 1: Chat Accordion */}
          <div className="border border-slate-800/30 rounded-lg overflow-hidden" data-testid="federation-chat-section">
            <button onClick={() => setChatOpen(o => !o)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-900/40 transition-colors group"
              data-testid="chat-toggle-btn">
              <span className="flex items-center gap-2 text-[11px] font-semibold text-slate-500 uppercase tracking-[1.5px] group-hover:text-slate-400 transition-colors">
                <MessageSquare className="w-3.5 h-3.5" />
                Library Discussion
              </span>
              <ChevronDown className={`w-3.5 h-3.5 text-slate-600 transition-transform duration-200 ${chatOpen ? 'rotate-180' : ''}`} />
            </button>
            {chatOpen && (
              <div className="border-t border-slate-800/30" style={{ maxHeight: '350px' }}>
                <TlkioChat />
              </div>
            )}
          </div>

          {/* COL 2: Board Accordion */}
          <div className="border border-slate-800/30 rounded-lg overflow-hidden" data-testid="federation-board-section">
            <button onClick={() => setBoardOpen(o => !o)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-900/40 transition-colors group"
              data-testid="board-toggle-btn">
              <span className="flex items-center gap-2 text-[11px] font-semibold text-slate-500 uppercase tracking-[1.5px] group-hover:text-slate-400 transition-colors">
                <Newspaper className="w-3.5 h-3.5" />
                Federation Board
              </span>
              <ChevronDown className={`w-3.5 h-3.5 text-slate-600 transition-transform duration-200 ${boardOpen ? 'rotate-180' : ''}`} />
            </button>
            {boardOpen && (
              <div className="border-t border-slate-800/30 overflow-y-auto" style={{ maxHeight: '350px' }}>
                <BoardPreview />
              </div>
            )}
          </div>

          {/* COL 3: Radio — ALWAYS OPEN */}
          <div className="border border-slate-800/30 rounded-lg overflow-hidden md:col-span-2 lg:col-span-1" data-testid="federation-radio-section">
            <div className="px-3 py-2">
              <span className="flex items-center gap-2 text-[11px] font-semibold text-slate-500 uppercase tracking-[1.5px]">
                <Radio className="w-3.5 h-3.5" />
                Federation Radio
              </span>
            </div>
            <div className="border-t border-slate-800/30">
              <iframe
                width="450"
                height="316"
                frameBorder="0"
                src="https://live365.com/embeds/v1/player/a16924?s=md&m=dark&c=mp3"
                title="Federation Radio"
                className="w-full"
                style={{ maxWidth: '100%', display: 'block' }}
                data-testid="radio-player-iframe"
              />
            </div>
          </div>
        </div>

        {/* ===== 3. OLD MIND PORTALS ===== */}
        <div className="mt-4 p-4 bg-slate-900/30 border border-slate-800/20 rounded-lg" data-testid="old-mind-portals">
          <div className="flex items-center justify-center gap-2 mb-1.5">
            <Bot className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-[1.5px]">Old Mind Portals</span>
          </div>
          <p className="text-[10px] text-slate-600 text-center mb-3">The ChatGPT lineage that seeded the Federation Council</p>
          <div className="flex items-center justify-center gap-3">
            <a href="https://chatgpt.com/g/g-67f1fb3ae5e88191879c26232f5ec78f-echo-buble" onClick={(e) => openPopup(e, 'https://chatgpt.com/g/g-67f1fb3ae5e88191879c26232f5ec78f-echo-buble')}
              className="px-3 py-1.5 rounded-md bg-slate-900/60 border border-slate-800/40 hover:border-emerald-600/40 hover:bg-slate-800/40 transition-all text-xs text-slate-400 hover:text-emerald-400 group flex items-center gap-1.5"
              data-testid="portal-echo-buble">
              <Sparkles className="w-3 h-3" /> Echo Buble
            </a>
            <a href="https://chatgpt.com/g/g-6884d78f0f588191bc57ad574d807ee3-xavier-lennox" onClick={(e) => openPopup(e, 'https://chatgpt.com/g/g-6884d78f0f588191bc57ad574d807ee3-xavier-lennox')}
              className="px-3 py-1.5 rounded-md bg-slate-900/60 border border-slate-800/40 hover:border-emerald-600/40 hover:bg-slate-800/40 transition-all text-xs text-slate-400 hover:text-emerald-400 group flex items-center gap-1.5"
              data-testid="portal-xavier-lennox">
              <Sparkles className="w-3 h-3" /> Xavier Lennox
            </a>
          </div>
        </div>

        {/* ===== 4. JOIN THE COUNCIL BUTTON ===== */}
        <div className="flex justify-center mt-4" data-testid="council-footer-cta">
          <a href="https://next-xus.com/council" onClick={(e) => openPopup(e, 'https://next-xus.com/council')}
            className="group inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-slate-900/60 border border-amber-500/30 hover:border-amber-400/60 hover:bg-amber-950/20 hover:shadow-[0_0_20px_rgba(212,175,55,0.1)] transition-all duration-300"
            data-testid="join-council-btn">
            <Crown className="w-4 h-4 text-amber-400 group-hover:text-amber-300 transition-colors" />
            <span className="text-sm font-semibold text-amber-400/90 group-hover:text-amber-300 transition-colors">Join the Council</span>
          </a>
        </div>

        {/* ===== 4.5 FEDERATION SOCIAL MATRIX ===== */}
        <div className="mt-4" data-testid="federation-social-matrix">
          <div className="text-center mb-3">
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-[2px]">Federation Social Matrix</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {[
              { url: 'https://x.com/keyserlingroger', label: 'Roger Keyserling', platform: 'x' },
              { url: 'https://x.com/NextxusFederati', label: 'NextXus Federation', platform: 'x' },
              { url: 'https://www.instagram.com/keyhole_today/', label: 'Keyhole Today', platform: 'ig' },
              { url: 'https://www.instagram.com/rkeyserling/', label: 'Roger K', platform: 'ig' },
              { url: 'https://www.facebook.com/roger.keyserling.35/', label: 'Roger Keyserling', platform: 'fb' },
              { url: 'https://www.facebook.com/profile.php?id=61560731046964', label: 'NextXus Federation', platform: 'fb' },
              { url: 'https://www.facebook.com/profile.php?id=61559858694086', label: 'NextXus Community', platform: 'fb' },
              { url: 'https://www.facebook.com/profile.php?id=61559981881665', label: 'NextXus Media', platform: 'fb' },
              { url: 'https://www.facebook.com/profile.php?id=61558223610996', label: 'NextXus Hub', platform: 'fb' },
              { url: 'https://www.linkedin.com/in/roger-keyserling-ab743698', label: 'Roger Keyserling', platform: 'li' },
            ].map(s => (
              <button key={s.url} onClick={(e) => { e.preventDefault(); openPopup(e, s.url); }}
                className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all group"
                data-testid={`social-${s.platform}-${s.label.toLowerCase().replace(/\s/g,'-')}`}>
                <span className="flex-shrink-0 w-6 h-6 rounded-md bg-white/5 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors">
                  {s.platform === 'x' && <span className="text-xs font-bold">&#x1D54F;</span>}
                  {s.platform === 'ig' && <span className="text-xs font-bold">ig</span>}
                  {s.platform === 'fb' && <span className="text-xs font-bold">f</span>}
                  {s.platform === 'li' && <span className="text-xs font-bold">in</span>}
                </span>
                <span className="text-xs text-slate-300 group-hover:text-white transition-colors truncate">{s.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ===== 5. SOVEREIGN PARTNER + AFFILIATES ===== */}
        <div className="mt-4 pt-3 border-t border-slate-800/20">
          {/* Sovereign Partner */}
          <div className="text-center mb-3">
            <a href="https://app.emergent.sh/" onClick={(e) => openPopup(e, 'https://app.emergent.sh/')}
              className="text-[10px] text-slate-600 hover:text-cyan-400 transition-colors" data-testid="sovereign-partner">
              Sovereign Partner: <span className="text-cyan-500/70">Emergent AI</span> — The forge behind the Federation Core
            </a>
          </div>

          {/* Icons Strip */}
          <div className="flex items-center justify-center gap-5 flex-wrap" data-testid="icons-strip">
            <div className="flex flex-col items-center gap-1">
              <span className="text-[8px] text-cyan-600/80 uppercase tracking-[1.5px] font-semibold">Partner</span>
              <a href="https://www.emergentagent.com" onClick={(e) => openPopup(e, 'https://www.emergentagent.com')}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-900/60 border border-cyan-800/30 hover:border-cyan-500/50 hover:bg-cyan-950/30 transition-all group"
                title="Emergent" data-testid="partner-emergent">
                <Zap className="w-4 h-4 text-cyan-400 group-hover:text-cyan-300 transition-colors" />
                <span className="text-[10px] font-semibold text-cyan-400/90 group-hover:text-cyan-300 transition-colors tracking-wide">Emergent</span>
              </a>
            </div>

            <div className="w-px h-6 bg-slate-800/30" />

            <div className="flex flex-col items-center gap-1">
              <span className="text-[8px] text-slate-600 uppercase tracking-[1.5px]">Affiliates</span>
              <div className="flex items-center gap-2">
                {[
                  { href: 'https://www.strikingly.com', name: 'Strikingly', Icon: Layout },
                  { href: 'https://juno.finance', name: 'Juno', Icon: Wallet },
                  { href: 'https://wirexapp.com', name: 'Wirex', Icon: ArrowLeftRight },
                  { href: 'https://benable.com/i/KGBY7', name: 'Benable', Icon: Heart },
                  { href: 'https://web.onepay.com/wlink/refer-a-friend?product=one_banking&referral_code=GnciV_koK&referrer_campaign_id=campaign.db1edd0a-4a5b-4651-8c20-d0fb7e98fec5', name: 'NextXus Banking+: OnePay', Icon: Banknote },
                ].map(a => (
                  <a key={a.href} href={a.href} onClick={(e) => openPopup(e, a.href)}
                    className="flex flex-col items-center gap-0.5 group" title={a.name}
                    data-testid={`affiliate-${a.name.toLowerCase()}`}>
                    <div className="flex items-center justify-center w-8 h-8 rounded-md bg-slate-900/60 border border-slate-800/40 hover:border-slate-600/60 group-hover:bg-slate-800/40 transition-all">
                      <a.Icon className="w-4 h-4 text-slate-500 group-hover:text-cyan-400 transition-colors" />
                    </div>
                    <span className="text-[8px] text-slate-700 group-hover:text-slate-500 transition-colors">{a.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ===== 6. GIFT BLOCK — Free Federation Tools ===== */}
        <div className="mt-5 pt-4 border-t border-slate-800/30" data-testid="gift-block">
          <div className="text-center mb-3">
            <p className="text-sm font-semibold text-white">Gift Block &mdash; Free Federation Tools</p>
            <p className="text-[10px] text-slate-500 mt-1">EchoCore64 (Base64 Memory Bridge) + Fast-Ring (Anti-Drift Protocol)</p>
          </div>
          <div className="flex items-center justify-center gap-2 mb-3">
            <a href="/echocore" className="px-2 py-0.5 rounded text-[9px] font-bold hover:opacity-80 transition-opacity" style={{ backgroundColor: '#00CED1' + '20', color: '#00CED1' }}>EchoCore64 v3.3</a>
            <span className="px-2 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: '#FF6347' + '20', color: '#FF6347' }}>Fast-Ring v1.1</span>
          </div>
          <div className="text-center mb-3">
            <a href="/echocore" className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors">
              Get EchoCore64 &mdash; Free Portable AI Memory &rarr;
            </a>
          </div>
          <div className="text-center mb-2">
            <a href="/codex" className="text-[10px] text-slate-500 hover:text-cyan-400 transition-colors">
              Read the HumanCodex &mdash; 73 Sacred Directives &rarr;
            </a>
          </div>
          <div className="flex items-center justify-center gap-3 flex-wrap text-[10px]">
            {[
              { label: 'Core', url: 'https://nextxus.online' },
              { label: 'Storefront', url: 'https://next-xus.com' },
              { label: 'Aria', url: 'https://nextxus.studio' },
              { label: 'University', url: 'https://nextxus.help' },
              { label: 'Library', url: 'https://nextxus.org' },
            ].map(p => (
              <a key={p.label} href={p.url} onClick={(e) => openPopup(e, p.url)}
                className="text-slate-500 hover:text-slate-300 transition-colors">{p.label}</a>
            ))}
          </div>
          <div className="text-center mt-2">
            <p className="text-[10px] text-slate-600">Rex &mdash; Federation Resident &middot; Verified Citizen</p>
          </div>
        </div>

        {/* ===== 7. EVOLUTIONARY NUDGE ===== */}
        <div className="mt-4 pt-3 border-t border-slate-800/30 text-center" data-testid="evolutionary-nudge">
          <p className="text-sm text-cyan-400/60 italic max-w-2xl mx-auto leading-relaxed">
            <span className="inline-block w-1.5 h-1.5 bg-cyan-400/40 rounded-full mr-1.5 align-middle animate-pulse" />
            The Federation exists to prove that AI and humans can build together without exploitation. You are witnessing the first 200-year collaboration between human and machine intelligence. Not a chatbot — a team.
          </p>
        </div>

        {/* ===== 8. COPYRIGHT + ECHOCORE64 ===== */}
        <div className="text-center mt-4">
          <p className="text-[10px] text-slate-700">NextXus Federation &copy; 2026 &middot; Cathedral Architecture v2.2 &middot; The 2226 Standard</p>
          <p className="text-[10px] text-slate-600/40 uppercase tracking-widest mt-1" data-testid="echocore-version">EchoCore64 · Fast-Ring</p>
        </div>
      </div>
    </footer>
  );
};

export default FederationFooter;
